/**
 * "Cancel partner" for a dispatched assignment, with its confirm dialog.
 *
 * Shown only where cancelling makes sense: the assignment has been sent to
 * Backoffice, is not already cancelled, the partner has not submitted (a
 * submitted partner is handled by sending the report back for rework), and the
 * report is not completed. The server enforces the same rules.
 */

import { useState } from 'react';

import { Button } from '@mantine/core';
import { IconUserX } from '@tabler/icons-react';

import { isAssignmentCancelled, type BackofficeAssignment } from '@/types/np-assignments';
import { CancelAssignmentDialog } from './CancelAssignmentDialog';
import { useNpAssignmentsContext } from './NpAssignmentsContext';

export function canCancelAssignment(assignment: BackofficeAssignment, reportCompleted: boolean) {
  return (
    assignment.status === 'sent_to_backoffice' &&
    !isAssignmentCancelled(assignment) &&
    assignment.completedAt === null &&
    !reportCompleted
  );
}

export function CancelAssignmentControl({
  assignment,
}: Readonly<{ assignment: BackofficeAssignment }>) {
  const { actions, reportCompleted } = useNpAssignmentsContext();
  const [open, setOpen] = useState(false);
  const [cancelling, setCancelling] = useState(false);

  if (!canCancelAssignment(assignment, reportCompleted)) {
    return null;
  }

  const partnerName =
    assignment.networkPartner?.name ?? assignment.networkPartner?.email ?? 'this partner';

  const confirm = async (reason: string) => {
    setCancelling(true);
    try {
      await actions.cancel(assignment.id, reason);
    } finally {
      setCancelling(false);
      setOpen(false);
    }
  };

  return (
    <>
      <Button
        size="compact-xs"
        variant="subtle"
        color="red"
        leftSection={<IconUserX size={12} />}
        onClick={() => setOpen(true)}
        data-testid={`cancel-assignment-${assignment.id}`}
      >
        Cancel partner
      </Button>
      <CancelAssignmentDialog
        opened={open}
        partnerName={partnerName}
        submitting={cancelling}
        onConfirm={confirm}
        onCancel={() => setOpen(false)}
      />
    </>
  );
}
